import { useEffect, useState, ChangeEvent, useCallback } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import UploadCaption, { BestUploadSizes } from "modules/uploader/UploadCaption";
import { ICompany } from "modules/company/types";
import { selectIsMobile } from "modules/deviceInfo";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { useSelector } from "react-redux";
import { ImageUploader } from "modules/uploader";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import ImagePlaceHolder from "../uploader/ImagePlaceHolder";
import { CompanyLogo } from "../company/components/CompanyLogo";
import * as FileStoragePaths from "../../services/firebaseService/storagePaths";
import useCompany from "../company/hooks/useCompany";
import { useStyles } from "./EditCompanyStyles";

const EditCompany = () => {
  const classes = useStyles();
  const isMobileView = useSelector(selectIsMobile);
  const { company, saveCompany, loadCompany } = useCompany();
  const { t } = useI18n(["common", "settings", "server"]);
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const [isNameEditMode, setIsNameEditMode] = useState(false);
  const [name, setName] = useState({
    error: false,
    value: "",
    message: "",
  });
  const [logoURL, setLogoURL] = useState({
    error: false,
    value: "",
    message: "",
    touched: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { myCompanyPlan } = useCompanyPlanSettings();

  useEffect(() => {
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (company) {
      setName({ error: false, value: company.name, message: "" });
      setLogoURL({
        error: false,
        value: company.logoURL || "",
        message: "",
        touched: false,
      });
    }
  }, [company]);

  const toggleEditName = () => {
    setIsNameEditMode(!isNameEditMode);
  };

  const saveChanges = useCallback(
    async (data: Partial<ICompany>) => {
      if (company && company.id) {
        setIsLoading(true);
        const response = await saveCompany({ id: company.id, ...data });

        if (!response || !response.isSuccess) {
          addErrorNotification({
            message: response?.message
              ? t(response.message)
              : t("settings:fail.save.company"),
            autoDismiss: 5,
          });
        } else {
          addSuccessNotification({ message: t("saved.success") });
        }
        setIsLoading(false);
      }
    },
    [addErrorNotification, addSuccessNotification, company, saveCompany, t],
  );

  useEffect(() => {
    if (logoURL.touched) {
      saveChanges({ ...company, logoURL: logoURL.value });
      setLogoURL({ ...logoURL, touched: false });
    }
  }, [logoURL, company, saveChanges]);

  const handleSaveName = () => {
    if (name.value) {
      const nameValue = name.value ? name.value.trim() : "";

      saveChanges({
        ...company,
        name: nameValue || "",
      });
      setName({ error: false, value: nameValue, message: "" });
    }
    setIsNameEditMode(false);
  };

  const onNameFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setName({ value, error: false, message: "" });
  };

  const onLogoFieldChange = (imageURL: string) => {
    setLogoURL({ value: imageURL, error: false, message: "", touched: true });
  };

  return (
    <div className={classes.editCompanyRoot}>
      <Typography variant="h3">{t("company.profile")}</Typography>
      {company && (
        <>
          {isLoading && <PageLoader message={t("wait")} />}
          <Box className={classes.boxContainer}>
            <Typography variant="h5" className={classes.companyName}>
              {t("company.name")}
            </Typography>
            <div className={classes.editCompanyContainer}>
              <Input
                disabled={!isNameEditMode}
                required
                fullWidth
                id="companyName"
                name="companyName"
                value={name.value}
                onChange={onNameFieldChange}
                inputProps={{
                  maxLength: 30,
                  fontSize: "20px",
                  fontWeight: 500,
                  "data-testid": "company-name-input",
                }}
                size="sm"
              />

              {!isNameEditMode && (
                <Button
                  onClick={toggleEditName}
                  variant="secondary"
                  data-testid="edit-name-button"
                  disabled={isTrialCompleted(myCompanyPlan)}
                  color="dark"
                  size="md"
                  className={classes.editButton}
                >
                  {t("button.edit")}
                </Button>
              )}
              {isNameEditMode && (
                <Button
                  size="md"
                  variant={isMobileView ? "secondary" : "ghost"}
                  color={isMobileView ? "blue" : "dark"}
                  onClick={handleSaveName}
                  disabled={!name.value}
                >
                  {t("button.save")}
                </Button>
              )}
            </div>
          </Box>

          <div className={classes.imageUploadContainer}>
            <Typography variant="h5" className={classes.companyPhoto}>
              {t("company.photo")}
            </Typography>
            <div className={classes.imageForm}>
              <div className={classes.companyImage}>
                {logoURL.value ? (
                  <CompanyLogo url={logoURL.value} />
                ) : (
                  <ImagePlaceHolder />
                )}
              </div>
              <div className={classes.imageDetails}>
                <ImageUploader
                  name="companyLogo"
                  level={FileStoragePaths.UPLOAD_LEVEL.COMPANY}
                  path={FileStoragePaths.COMPANY_LOGO}
                  onImageUploaded={onLogoFieldChange}
                  disabled={!name.value || isTrialCompleted(myCompanyPlan)}
                  variant="secondary"
                  color="blue"
                  size="md"
                  data-testid="change-photo-button"
                >
                  {t("settings:change.photo")}
                </ImageUploader>
              </div>
            </div>
            <UploadCaption bestSize={BestUploadSizes.big} allowGif />
          </div>
        </>
      )}
    </div>
  );
};

export default EditCompany;
